import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { COLLECTION_COLORS, getCollectionColorName } from '@maker-toolchest/shared';

interface CollectionColorPickerProps {
  selectedColor?: string;
  onSelect: (color: string) => void;
  label?: string;
}

export function CollectionColorPicker({
  selectedColor = '#6366f1',
  onSelect,
  label,
}: CollectionColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const colorName = getCollectionColorName(selectedColor);
  const isCustom = colorName === 'Custom';

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowCustomPicker(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleColorSelect = (color: string) => {
    onSelect(color);
    setIsOpen(false);
    setShowCustomPicker(false);
  };

  const handleCustomClick = () => {
    setShowCustomPicker(true);
  };

  const handleCustomColorChange = (newColor: string) => {
    onSelect(newColor);
  };

  return (
    <div className="w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-foreground">{label}</label>
      )}
      <div className="relative">
        {/* Dropdown Button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="block w-full appearance-none rounded-md border bg-background-subtle text-foreground
            focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary
            h-10 px-3 text-sm border-border
            flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <div
              className="h-5 w-5 rounded-full border border-border flex-shrink-0"
              style={{ backgroundColor: selectedColor }}
            />
            <span>{colorName}</span>
          </div>
          <ChevronDown className="h-4 w-4 text-foreground-muted" />
        </button>

        {/* Dropdown Menu - Fixed positioning to break out of modal */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="fixed z-[100] mt-1 rounded-md border border-border bg-background shadow-lg max-h-80 overflow-y-auto"
            style={{
              top: `${dropdownPosition.top + 4}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {/* Pre-defined Colors */}
            {COLLECTION_COLORS.map((color) => {
              const isSelected = selectedColor.toLowerCase() === color.value.toLowerCase();
              return (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => handleColorSelect(color.value)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 text-sm
                    hover:bg-background-subtle transition-colors
                    ${isSelected ? 'bg-primary/10' : ''}
                  `}
                >
                  <div
                    className="h-5 w-5 rounded-full border border-border flex-shrink-0"
                    style={{ backgroundColor: color.value }}
                  />
                  <span className="flex-1 text-left">{color.name}</span>
                  {isSelected && <Check className="h-4 w-4 text-primary" />}
                </button>
              );
            })}

            {/* Custom Color Option */}
            <div className="border-t border-border">
              <button
                type="button"
                onClick={handleCustomClick}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm
                  hover:bg-background-subtle transition-colors
                  ${isCustom ? 'bg-primary/10' : ''}
                `}
              >
                <div
                  className="h-5 w-5 rounded-full border border-border flex-shrink-0"
                  style={{
                    background: isCustom
                      ? selectedColor
                      : 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                  }}
                />
                <span className="flex-1 text-left">Custom</span>
                {isCustom && <Check className="h-4 w-4 text-primary" />}
              </button>

              {/* Custom Color Picker */}
              {showCustomPicker && (
                <div className="p-3 bg-background-subtle border-t border-border">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={selectedColor}
                      onChange={(e) => handleCustomColorChange(e.target.value)}
                      className="h-8 w-16 border border-border rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={selectedColor}
                      onChange={(e) => handleCustomColorChange(e.target.value)}
                      pattern="^#[0-9A-Fa-f]{6}$"
                      className="flex-1 px-2 py-1.5 bg-background border border-border rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                      placeholder="#6366f1"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
